import React, { useContext, useEffect, useRef, useState } from "react";
import { Box, Button, CircularProgress, Container, Stack } from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faUserShield } from "@fortawesome/free-solid-svg-icons";
import { useNavigate, useParams } from "react-router-dom";

import { DataContext } from "../../../context/DataContext";
import { getRuta } from "../../../helpers/Helpers";
import { tienePermiso } from "../../../helpers/permisos";
import { getToken } from "../../../api/Account";
import { getRolByIdAsync, getRolListAsync, updateRolAsync } from "../../../api/Rols";
import { EstadoError, PageHeader, useAviso } from "../../../components/base";
import {
  CargandoFormulario,
  llevarAlPrimerError,
  useCargaLocal,
  useConfirmacion,
  useEnvio,
  useEscritura,
} from "../comunes";
import FormularioRol, { validarRol } from "./FormularioRol";
import { filasParaServidor, permisosDesdeFilas, TODOS_LOS_PERMISOS } from "./permisos";
import { pedirTodo } from "./confirmaciones";

// Los roles (para el rol superior) y el rol llegan juntos: una sola carga con su "Reintentar".
const cargarTodo = async (token, id) => {
  const [roles, rol] = await Promise.all([getRolListAsync(token), getRolByIdAsync(token, id)]);
  if (!roles.statusResponse) return roles;
  if (!rol.statusResponse) return rol;
  if (!rol.data.result) return { statusResponse: false, error: "No se encontró el rol" };
  return { statusResponse: true, data: { roles: roles.data.result || [], rol: rol.data.result } };
};

const iguales = (a, b) => TODOS_LOS_PERMISOS.every((d) => Boolean(a[d]) === Boolean(b[d]));

const EditRol = () => {
  const token = getToken();
  let ruta = getRuta();
  let navigate = useNavigate();
  const { id } = useParams();
  const { access } = useContext(DataContext);
  const aviso = useAviso();
  const escribir = useEscritura();
  const { guardando, enviar } = useEnvio();
  const { pedir: pedirConfirmacion, dialogo: confirmacion } = useConfirmacion();
  const formulario = useRef(null);

  const puedeEditar = tienePermiso(access, "SECURITY UPDATE");

  const [roles, setRoles] = useState([]);
  // El rol tal como llegó: se reenvía completo (con isFullAccess, que no se toca) al guardar.
  const [rolOriginal, setRolOriginal] = useState(null);
  const [datos, setDatos] = useState({ rolName: "", idRolSuperior: "" });
  const [permisos, setPermisos] = useState({});
  const [guardado, setGuardado] = useState({ datos: null, permisos: {} });

  const [intentado, setIntentado] = useState(false);
  const errores = intentado ? validarRol(datos, roles) : {};

  const { cargando, error, cargar } = useCargaLocal(() => cargarTodo(token, id));

  const getDatos = async () => {
    const result = await cargar();
    if (!result) return;
    const { roles: listaRoles, rol } = result.data;
    const iniciales = { rolName: rol.rolName ?? "", idRolSuperior: rol.idRolSuperior };
    const marcados = permisosDesdeFilas(rol.permissions);
    setRoles(listaRoles);
    setRolOriginal(rol);
    setDatos(iniciales);
    setPermisos(marcados);
    setGuardado({ datos: iniciales, permisos: marcados });
  };

  useEffect(() => {
    getDatos();
  }, [id]);

  const hayCambios =
    guardado.datos !== null &&
    (datos.rolName !== guardado.datos.rolName ||
      datos.idRolSuperior !== guardado.datos.idRolSuperior ||
      !iguales(permisos, guardado.permisos));

  const onCampo = (campo, valor) => setDatos((actual) => ({ ...actual, [campo]: valor }));

  const descartar = () => {
    setDatos(guardado.datos);
    setPermisos(guardado.permisos);
    setIntentado(false);
  };

  const saveRol = () =>
    enviar(async () => {
      setIntentado(true);
      if (Object.keys(validarRol(datos, roles)).length > 0) {
        llevarAlPrimerError(formulario);
        return;
      }
      // Se envían los 46 permisos del catálogo (todos se muestran). Las filas que el editor no
      // muestra no viajan y el servidor no las toca.
      const ok = await escribir(
        () =>
          updateRolAsync(token, {
            ...rolOriginal,
            rolName: datos.rolName.trim(),
            isFullAccess: rolOriginal.isFullAccess,
            idRolSuperior: datos.idRolSuperior,
            permissions: filasParaServidor(permisos),
          }),
        "No se pudo actualizar el rol"
      );
      if (!ok) return;
      setIntentado(false);
      setGuardado({ datos: { ...datos, rolName: datos.rolName.trim() }, permisos });
      aviso.exito(`Rol ${datos.rolName.trim()} actualizado`);
    });

  return (
    <Container maxWidth={false} disableGutters>
      <PageHeader
        titulo={puedeEditar ? "Editar rol" : "Permisos del rol"}
        subtitulo={guardado.datos && !cargando ? guardado.datos.rolName : undefined}
        icono={<FontAwesomeIcon icon={faUserShield} />}
        acciones={
          <Button
            variant="outlined"
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate(`${ruta}/rols`)}
          >
            Regresar
          </Button>
        }
      />

      {error ? (
        <EstadoError
          titulo={
            error === "conexion"
              ? "No se pudo conectar con el servidor"
              : "No se pudo cargar el rol"
          }
          mensaje="Revisa tu conexión e inténtalo de nuevo."
          onReintentar={getDatos}
        />
      ) : cargando || !rolOriginal ? (
        <CargandoFormulario etiqueta="Cargando el rol…" />
      ) : (
        <Stack spacing={3} ref={formulario}>
          <FormularioRol
            datos={datos}
            onCampo={onCampo}
            permisos={permisos}
            onPermisos={setPermisos}
            onTodo={puedeEditar ? (valor) => pedirTodo(pedirConfirmacion, valor, setPermisos) : undefined}
            roles={roles}
            errores={errores}
            deshabilitado={guardando || !puedeEditar}
            idPropio={rolOriginal.id}
            esRaiz={rolOriginal.idRolSuperior === rolOriginal.id}
          />

          {puedeEditar ? (
            <Stack
              direction={{ xs: "column-reverse", sm: "row" }}
              spacing={1.5}
              justifyContent="flex-end"
            >
              <Button
                variant="text"
                size="large"
                disabled={guardando || !hayCambios}
                onClick={descartar}
              >
                Descartar cambios
              </Button>
              <Box sx={{ display: "flex" }}>
                <Button
                  variant="contained"
                  size="large"
                  disabled={guardando || !hayCambios}
                  onClick={() => saveRol()}
                  startIcon={guardando ? <CircularProgress size={18} color="inherit" /> : null}
                  sx={{ width: { xs: "100%", sm: "auto" }, minWidth: 220 }}
                >
                  {guardando ? "Guardando…" : "Guardar cambios"}
                </Button>
              </Box>
            </Stack>
          ) : null}
        </Stack>
      )}

      {confirmacion}
    </Container>
  );
};

export default EditRol;
